import { Text, Element as SlateElement } from 'slate';
import { fontFamilyMap } from './SlateUtilityFunctions';

// DESERIALIZATION: HTML to Slate
const ELEMENT_TAGS = {
    A: (el) => ({ type: 'link', href: el.getAttribute('href'), target: el.getAttribute('target') || '_blank' }),
    BLOCKQUOTE: () => ({ type: 'blockquote' }),
    H1: () => ({ type: 'headingOne' }),
    H2: () => ({ type: 'headingTwo' }),
    H3: () => ({ type: 'headingThree' }),
    H4: () => ({ type: 'headingFour' }),
    H5: () => ({ type: 'headingFive' }),
    H6: () => ({ type: 'headingSix' }),
    IMG: (el) => ({ type: 'image', url: el.getAttribute('src') }),
    LI: () => ({ type: 'list-item' }),
    OL: () => ({ type: 'orderedList' }),
    P: () => ({ type: 'paragraph' }),
    PRE: () => ({ type: 'code' }),
    UL: () => ({ type: 'unorderedList' }),
    TABLE: (el) => {
        const hasHeader = el.querySelector('thead') || el.querySelector('th');
        return { type: hasHeader ? 'table' : 'tableWithoutHeaders' };
    },
    THEAD: () => ({ type: 'table-head' }),
    TBODY: () => ({ type: 'table-body' }),
    TR: () => ({ type: 'table-row' }),
    TD: () => ({ type: 'table-cell' }),
    TH: () => ({ type: 'table-header' }),
    DIV: () => ({ type: 'paragraph' }),
};

const TEXT_TAGS = {
    CODE: () => ({ code: true }),
    DEL: () => ({ strikethrough: true }),
    EM: () => ({ italic: true }),
    I: () => ({ italic: true }),
    S: () => ({ strikethrough: true }),
    STRIKE: () => ({ strikethrough: true }),
    STRONG: () => ({ bold: true }),
    B: () => ({ bold: true }),
    U: () => ({ underline: true }),
    SUP: () => ({ superscript: true }),
    SUB: () => ({ subscript: true }),
    FONT: (el) => {
        const attrs = {};
        const color = el.getAttribute('color');
        if (color) attrs.color = color;
        return attrs;
    },
};

// Parse inline styles from style attribute
const parseStyles = (style) => {
    const styles = {};
    if (!style) return styles;

    try {
        const declarations = style.split(';').filter((decl) => decl.trim());

        declarations.forEach((decl) => {
            const colonIndex = decl.indexOf(':');
            if (colonIndex === -1) return;

            const property = decl.substring(0, colonIndex).trim();
            const value = decl.substring(colonIndex + 1).trim();

            if (!value) return;

            switch (property) {
                case 'color':
                    // Normalize color value (remove quotes, extra spaces)
                    styles.color = value.replace(/['"]/g, '').trim();
                    break;
                case 'background-color':
                    styles.bgColor = value.replace(/['"]/g, '').trim();
                    break;
                case 'font-size':
                    styles.fontSize = value;
                    break;
                case 'font-family':
                    const cleanValue = value.replace(/['"]/g, '');
                    const familyKey = Object.keys(fontFamilyMap).find((key) => fontFamilyMap[key].includes(cleanValue));
                    if (familyKey) {
                        styles.fontFamily = familyKey;
                    } else {
                        styles.fontFamily = cleanValue;
                    }
                    break;
                case 'text-align':
                    styles.textAlign = value;
                    break;
                case 'font-weight':
                    const weight = parseInt(value);
                    if (value === 'bold' || value === 'bolder' || (!isNaN(weight) && weight >= 600)) {
                        styles.bold = true;
                    } else if (value === 'normal' || (!isNaN(weight) && weight < 600)) {
                        styles.bold = false;
                    }
                    break;
                case 'font-style':
                    if (value === 'italic' || value === 'oblique') {
                        styles.italic = true;
                    }
                    break;
                case 'text-decoration':
                case 'text-decoration-line':
                    if (value.includes('underline')) {
                        styles.underline = true;
                    }
                    if (value.includes('line-through')) {
                        styles.strikethrough = true;
                    }
                    break;
            }
        });

        return styles;
    } catch (error) {
        console.error('Error parsing styles:', error);
        return {};
    }
};

const decodeHtmlEntities = (text) => {
    const textArea = document.createElement('textarea');
    textArea.innerHTML = text;
    return textArea.value;
};

// CRITICAL FIX: Create objects that are extensible
const createTextNode = (text, marks = {}) => {
    return {
        text: text || '',
        ...marks,
    };
};

const createElementNode = (type, attributes = {}, children = []) => {
    return {
        type,
        children: children.length > 0 ? children : [createTextNode('')],
        ...attributes,
    };
};

// Deserialize HTML to Slate nodes
const deserialize = (el, markAttributes = {}) => {
    try {
        if (el.nodeType === Node.TEXT_NODE) {
            const text = el.textContent || '';

            if (text === '') {
                return null;
            }

            const hasFormatting = Object.keys(markAttributes).length > 0;
            if (text.trim() === '' && text !== ' ' && !hasFormatting) {
                return null;
            }

            return createTextNode(text, markAttributes);
        }

        if (el.nodeType !== Node.ELEMENT_NODE) {
            return null;
        }

        const nodeName = el.nodeName;

        // Handle BR tags - return a special marker to indicate line break
        if (nodeName === 'BR') {
            return { __BR_MARKER__: true };
        }

        // Handle PRE > CODE structure
        if (nodeName === 'PRE' && el.childNodes[0] && el.childNodes[0].nodeName === 'CODE') {
            const codeContent = el.childNodes[0].textContent || '';
            return createElementNode('code', {}, [createTextNode(codeContent, markAttributes)]);
        }

        // Accumulate marks from current element
        let currentMarks = { ...markAttributes };

        // Check if parent marks have inline styles (color, bgColor, etc.) but no explicit bold
        // This indicates we're inside a styled element from Google Docs/Word
        const parentHasStyles = markAttributes.color || markAttributes.bgColor || markAttributes.fontSize;
        const parentHasBold = markAttributes.bold !== undefined;

        // Process text formatting tags (including FONT)
        if (TEXT_TAGS[nodeName]) {
            // For STRONG/B tags: if we're inside a styled parent without explicit bold,
            // ignore the STRONG tag (it's likely spurious from Google Docs)
            if ((nodeName === 'STRONG' || nodeName === 'B') && parentHasStyles && !parentHasBold) {
                // Skip the STRONG tag - parent has inline styles but didn't specify font-weight
                // This means the source (Google Docs) didn't want it bold
            } else {
                const textAttrs = TEXT_TAGS[nodeName](el);
                currentMarks = { ...currentMarks, ...textAttrs };
            }
        }

        // Process SPAN with inline styles (in addition to any TEXT_TAGS)
        const elementStyle = el.getAttribute('style');
        if (nodeName === 'SPAN' && elementStyle) {
            const inlineStyles = parseStyles(elementStyle);
            currentMarks = { ...currentMarks, ...inlineStyles };
        }

        // Clean up marks: remove properties that are explicitly false (like bold: false)
        // In Slate, we omit properties instead of setting them to false
        Object.keys(currentMarks).forEach((key) => {
            if (currentMarks[key] === false) {
                delete currentMarks[key];
            }
        });

        // Process children with accumulated marks
        const processedChildren = [];

        Array.from(el.childNodes).forEach((child) => {
            const result = deserialize(child, currentMarks);
            if (result) {
                if (Array.isArray(result)) {
                    processedChildren.push(...result);
                } else {
                    processedChildren.push(result);
                }
            }
        });

        // Handle block elements
        if (ELEMENT_TAGS[nodeName]) {
            const attrs = ELEMENT_TAGS[nodeName](el);

            // Add text alignment from styles
            const blockStyles = parseStyles(el.getAttribute('style'));
            if (blockStyles.textAlign) {
                attrs.textAlign = blockStyles.textAlign;
            }

            // Ensure we have valid children
            let validChildren = processedChildren.length > 0 ? processedChildren : [createTextNode('', currentMarks)];

            switch (attrs.type) {
                case 'image':
                    return createElementNode(
                        'image',
                        {
                            url: el.getAttribute('src'),
                            alt: el.getAttribute('alt') || '',
                            width: el.getAttribute('width'),
                            height: el.getAttribute('height'),
                        },
                        [createTextNode('')],
                    );

                case 'link':
                    // Links are inline elements and should only have text children, not paragraphs
                    // If children contain paragraphs, flatten them to text nodes
                    const linkChildren = validChildren.flatMap((child) => {
                        if (child.type === 'paragraph' && child.children) {
                            // Extract text nodes from paragraph
                            return child.children;
                        }
                        return child;
                    });

                    return createElementNode(
                        'link',
                        {
                            url: el.getAttribute('href'),
                            target: el.getAttribute('target') || '_blank',
                        },
                        linkChildren.length > 0 ? linkChildren : [createTextNode('')],
                    );

                case 'unorderedList':
                case 'orderedList':
                    // For lists, ensure children are list items
                    // Filter out any non-element nodes and ensure proper structure
                    const listItems = validChildren
                        .filter((child) => child.type === 'list-item' || child.type === 'paragraph')
                        .map((child) => {
                            // If we got a paragraph, convert it to a list-item
                            if (child.type === 'paragraph') {
                                return createElementNode('list-item', {}, child.children);
                            }
                            return child;
                        });

                    validChildren = listItems.length > 0 ? listItems : [createElementNode('list-item', {}, [createTextNode('')])];
                    return createElementNode(attrs.type, attrs, validChildren);

                case 'list-item':
                    // Ensure list item has proper paragraph children
                    const hasBlockChildren = validChildren.some((child) => child.type !== undefined && child.text === undefined);
                    if (!hasBlockChildren) {
                        // Wrap text nodes in a paragraph
                        validChildren = [createElementNode('paragraph', {}, validChildren)];
                    }
                    return createElementNode(attrs.type, attrs, validChildren);

                case 'paragraph':
                    // Handle BR markers - split into multiple paragraphs
                    const hasBrMarkers = validChildren.some((child) => child.__BR_MARKER__);

                    if (hasBrMarkers) {
                        const paragraphs = [];
                        let currentParagraphChildren = [];

                        // Extract only paragraph-level attributes (textAlign, etc.)
                        const paragraphAttrs = { type: 'paragraph' };
                        if (attrs.textAlign) {
                            paragraphAttrs.textAlign = attrs.textAlign;
                        }

                        validChildren.forEach((child) => {
                            if (child.__BR_MARKER__) {
                                // Found a BR - create a paragraph with current children
                                if (currentParagraphChildren.length > 0) {
                                    paragraphs.push(createElementNode('paragraph', paragraphAttrs, currentParagraphChildren));
                                } else {
                                    // Empty line - create empty paragraph
                                    paragraphs.push(createElementNode('paragraph', paragraphAttrs, [createTextNode('')]));
                                }
                                currentParagraphChildren = [];
                            } else {
                                currentParagraphChildren.push(child);
                            }
                        });

                        // Add remaining children as final paragraph
                        if (currentParagraphChildren.length > 0) {
                            paragraphs.push(createElementNode('paragraph', paragraphAttrs, currentParagraphChildren));
                        }

                        // Return array of paragraphs
                        return paragraphs.length > 0 ? paragraphs : createElementNode('paragraph', paragraphAttrs, [createTextNode('')]);
                    }

                    // No BR markers - return single paragraph
                    return createElementNode('paragraph', attrs, validChildren);

                default:
                    return createElementNode(attrs.type, attrs, validChildren);
            }
        }

        // For formatting elements (including SPAN and FONT with styles), return the processed children
        const hasInlineStyles = nodeName === 'SPAN' && Object.keys(parseStyles(el.getAttribute('style'))).length > 0;
        const isFontTag = nodeName === 'FONT';

        if (TEXT_TAGS[nodeName] || hasInlineStyles || isFontTag) {
            return processedChildren;
        }

        // For unknown elements, wrap children in a paragraph
        if (processedChildren.length > 0) {
            // If we have text nodes with marks, preserve them
            const hasTextNodes = processedChildren.some((child) => child.text !== undefined);
            if (hasTextNodes) {
                return createElementNode('paragraph', {}, processedChildren);
            }
            return processedChildren;
        }

        return null;
    } catch (error) {
        console.error('Error deserializing node:', el.nodeName, error);
        // Return plain text as fallback
        const text = el.textContent || '';
        if (text.trim()) {
            return createTextNode(text, markAttributes);
        }
        return null;
    }
};

// SERIALIZATION: Slate to HTML
const serialize = (node) => {
    if (Text.isText(node)) {
        let string = escapeHtml(node.text);

        if (node.mention && node.mentionUserId) {
            string = `<span class="text-editor-mention" data-mention-user-id="${node.mentionUserId || ''}">${string}</span>`;
        }

        if (node.bold) {
            string = `<strong>${string}</strong>`;
        }
        if (node.italic) {
            string = `<em>${string}</em>`;
        }
        if (node.underline) {
            string = `<u>${string}</u>`;
        }
        if (node.strikethrough) {
            string = `<s>${string}</s>`;
        }
        if (node.code) {
            string = `<code>${string}</code>`;
        }
        if (node.superscript) {
            string = `<sup>${string}</sup>`;
        }
        if (node.subscript) {
            string = `<sub>${string}</sub>`;
        }

        // Apply inline styles
        const styles = [];
        if (node.color) styles.push(`color: ${node.color}`);
        if (node.bgColor) styles.push(`background-color: ${node.bgColor}`);
        if (node.fontSize) styles.push(`font-size: ${node.fontSize}`);
        if (node.fontFamily && fontFamilyMap[node.fontFamily]) {
            styles.push(`font-family: ${fontFamilyMap[node.fontFamily]}`);
        }

        if (styles.length > 0) {
            string = `<span style="${styles.join('; ')}">${string}</span>`;
        }

        return string;
    }

    const children = node.children.map((n) => serialize(n)).join('');

    switch (node.type) {
        case 'paragraph':
            const isEmpty = node.children.length === 1 && node.children[0].text === '' && !node.children[0].mention;

            if (isEmpty) {
                return '<br/>';
            }
            const alignStyle = node.textAlign ? ` style="text-align: ${node.textAlign}"` : '';
            return `<div${alignStyle}>${children}</div>`;
        case 'headingOne':
            return `<h1${node.textAlign ? ` style="text-align: ${node.textAlign}"` : ''}>${children}</h1>`;
        case 'headingTwo':
            return `<h2${node.textAlign ? ` style="text-align: ${node.textAlign}"` : ''}>${children}</h2>`;
        case 'headingThree':
            return `<h3${node.textAlign ? ` style="text-align: ${node.textAlign}"` : ''}>${children}</h3>`;
        case 'headingFour':
            return `<h4${node.textAlign ? ` style="text-align: ${node.textAlign}"` : ''}>${children}</h4>`;
        case 'headingFive':
            return `<h5${node.textAlign ? ` style="text-align: ${node.textAlign}"` : ''}>${children}</h5>`;
        case 'headingSix':
            return `<h6${node.textAlign ? ` style="text-align: ${node.textAlign}"` : ''}>${children}</h6>`;
        case 'blockquote':
            return `<blockquote${node.textAlign ? ` style="text-align: ${node.textAlign}"` : ''}>${children}</blockquote>`;
        case 'unorderedList':
            return `<ul>${children}</ul>`;
        case 'orderedList':
            return `<ol>${children}</ol>`;
        case 'list-item':
            return `<li>${children}</li>`;
        case 'link':
            const href = node.href || node.url || '#';
            const target = node.target ? ` target="${node.target}"` : '';
            return `<a href="${escapeHtml(href)}"${target}>${children}</a>`;
        case 'table':
        case 'tableWithoutHeaders':
            return `<table>${children}</table>`;
        case 'table-head':
            return `<thead>${children}</thead>`;
        case 'table-body':
            return `<tbody>${children}</tbody>`;
        case 'table-row':
            return `<tr>${children}</tr>`;
        case 'table-header':
            return `<th>${children}</th>`;
        case 'table-cell':
            return `<td>${children}</td>`;
        case 'image':
            return `<img src="${escapeHtml(node.url)}" />`;
        case 'code':
            return `<pre><code>${children}</code></pre>`;
        default:
            return children;
    }
};

// Helper function to escape HTML and preserve whitespace
const escapeHtml = (unsafe) => {
    if (typeof unsafe !== 'string') return '';

    let escaped = unsafe.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');

    // Preserve multiple consecutive spaces by converting to &nbsp;
    // Keep alternating pattern for better word wrapping
    escaped = escaped.replace(/ {2,}/g, (match) => {
        let result = '';
        for (let i = 0; i < match.length; i++) {
            result += i % 2 === 0 ? '&nbsp;' : ' ';
        }
        return result;
    });

    return escaped;
};

// Public API functions
export const slateToHtml = (value) => {
    try {
        if (!value || !Array.isArray(value)) {
            return '';
        }
        return value.map((node) => serialize(node)).join('');
    } catch (error) {
        console.error('Error serializing Slate to HTML:', error);
        return '';
    }
};

export const htmlToSlate = (html) => {
    try {
        if (!html || html.trim() === '') {
            return [createElementNode('paragraph')];
        }

        const document = new DOMParser().parseFromString(html, 'text/html');
        let fragment = [];

        // Process each child node of the body
        Array.from(document.body.childNodes).forEach((node) => {
            const result = deserialize(node);
            if (result) {
                if (Array.isArray(result)) {
                    fragment.push(...result);
                } else {
                    fragment.push(result);
                }
            }
        });

        // Filter out null/undefined values
        fragment = fragment.filter(Boolean);

        // Ensure we return a valid Slate value
        if (fragment.length === 0) {
            return [createElementNode('paragraph')];
        }

        // Ensure all items are proper element nodes
        return fragment.map((item) => {
            if (item.type) {
                return item;
            } else if (item.text !== undefined) {
                // Wrap standalone text nodes in paragraphs
                return createElementNode('paragraph', {}, [item]);
            }
            return createElementNode('paragraph');
        });
    } catch (error) {
        console.error('Error deserializing HTML to Slate:', error);
        return [createElementNode('paragraph')];
    }
};

// Helper function to get plain text from Slate value
export const slateToPlainText = (value) => {
    try {
        if (!value || !Array.isArray(value)) return '';

        const extractText = (node) => {
            if (Text.isText?.(node)) {
                return node.text || '';
            } else if (node.children && Array.isArray(node.children)) {
                return node.children.map(extractText).join('');
            }
            return '';
        };

        return value.map(extractText).join('\n');
    } catch (error) {
        console.error('Error converting Slate to plain text:', error);
        return '';
    }
};
// Helper function to create Slate value from plain text
export const plainTextToSlate = (text) => {
    if (!text || text.trim() === '') {
        return [createElementNode('paragraph')];
    }

    const lines = text.split('\n');
    return lines.map((line) => createElementNode('paragraph', {}, [createTextNode(line)]));
};

export const cleanTrailingNewlines = (value) => {
    if (!value || value.length === 0) {
        return [{ type: 'paragraph', children: [{ text: '' }] }];
    }
    let cleanedValue = [...value];
    const isEmptyParagraph = (node) => {
        return (
            node &&
            node?.type === 'paragraph' &&
            node?.children &&
            node?.children?.length === 1 &&
            node?.children?.[0] &&
            node?.children?.[0]?.text === ''
        );
    };
    while (cleanedValue.length > 0 && isEmptyParagraph(cleanedValue[cleanedValue.length - 1])) {
        cleanedValue.pop();
    }
    while (cleanedValue.length > 0 && isEmptyParagraph(cleanedValue[0])) {
        cleanedValue.shift();
    }
    return cleanedValue.length > 0 ? cleanedValue : [{ type: 'paragraph', children: [{ text: '' }] }];
};

export default {
    slateToHtml,
    htmlToSlate,
    slateToPlainText,
    plainTextToSlate,
};
